import { useState, useEffect, useMemo } from 'react';
import { FieldHelp } from '@/features/guided-help';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Trophy,
  Users,
  Calendar,
  DollarSign,
  MapPin,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  CalendarDays,
  Loader2,
} from 'lucide-react';
import type { WizardStepProps } from '../../../engine/types';
import { getGenderLabel } from '@/utils/gender-utils';
import { getPaymentTypeLabel } from '@/utils/payment-utils';
import { formatDateForUser } from '@/utils/formatters';
import { getCourtsCountForSport } from '@/utils/club-utils';
import {
  FixtureType,
  Gender,
  OverflowGroupClassification,
  PaymentType,
  TournamentFormat,
  type Club,
} from '@/types/tournament.model';
import { showOverflowGroupClassificationField } from '@/utils/tournament-overflow-group';
import { resolveDescriptionReglamento } from '@/utils/tournament-reglamento';
import { getFixtureTypeLabel } from '@/utils/fixture-utils';
import {
  getTournamentFormatLabel,
  isAmericanFormat,
  getAmericanFinalBracketLabel,
  getAmericanGroupFormatLabel,
} from '@/utils/tournament-format';
import { getSchedulesByClub } from '@/api/schedule/scheduleApi';
import type { Schedule } from '@/types/schedule.model';
import type { WeeklyScheduleSlot } from '@/types/tournament-round.model';
import { dayOfWeekEnum, dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';
import DOMPurify from 'dompurify';
import {
  effectiveAgesFromTeamConfig,
  getTournamentAgeDisplayLines,
} from '@/utils/tournament-age';
import { getCategorySumLabel } from '@/utils/category-utils';
import { useEloCategories } from '@/hooks/useEloCategories';

/**
 * Configuration for the SummaryStep component.
 */
export interface SummaryStepConfig {
  showBanner?: boolean;
  showPrizes?: boolean;
  showDescription?: boolean;
  showCategories?: boolean;
}

const DEFAULT_CONFIG: SummaryStepConfig = {
  showBanner: true,
  showPrizes: true,
  showDescription: true,
  showCategories: true,
};

interface SummaryStepProps extends WizardStepProps<unknown> {
  config?: SummaryStepConfig;
}

function formatScheduleDateForSummary(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-AR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Days of the week for the LONG pattern (order + Spanish label).
const LONG_WEEK_DAYS: { value: WeeklyScheduleSlot['dayOfWeek']; label: string }[] = dayOfWeekEnum.options.map(
  (value) => ({ value, label: dayOfWeekMap[value] }),
);

// Template slots overlap (one per match start time, e.g. 08:00-09:30,
// 09:00-10:30...). We merge overlapping or touching ones to show the club's real
// opening range per day (e.g. 08:00-22:30). The 'HH:mm' format compares correctly as a string.
function mergeSlotRanges(
  slots: WeeklyScheduleSlot[],
): { start: string; end: string }[] {
  const sorted = [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));
  const ranges: { start: string; end: string }[] = [];
  for (const s of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && s.startTime <= last.end) {
      if (s.endTime > last.end) last.end = s.endTime;
    } else {
      ranges.push({ start: s.startTime, end: s.endTime });
    }
  }
  return ranges;
}

export function SummaryStep({ draft, config: userConfig }: SummaryStepProps) {
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  const {
    sport,
    basicInfo,
    teamConfig,
    fixtureConfig,
    pricing,
    clubSelection,
    schedule,
    scheduleSelection,
    tournamentConfig,
    clubSchedules,
  } = draft;

  // Shows the reglamento the tournament will be published with, now that the format is known.
  const descriptionPreview = useMemo(
    () => resolveDescriptionReglamento(basicInfo?.description ?? '', fixtureConfig),
    [basicInfo?.description, fixtureConfig],
  );

  const isAmerican = isAmericanFormat(fixtureConfig?.format);
  // LONG shows the chosen weekly grid; its rows are copied into weeklyScheduleTemplate for display.
  const isLong = fixtureConfig?.format === TournamentFormat.LONG;
  const weeklyTemplate = clubSchedules?.weeklyScheduleTemplate ?? [];

  const eloCategories = useEloCategories(sport?.sport?.uid);

  const categorySumLabel = getCategorySumLabel(
    teamConfig?.minCategory,
    teamConfig?.maxCategory,
    teamConfig?.forceSum,
    eloCategories,
  );

  // State for loaded schedules
  const [loadedSchedules, setLoadedSchedules] = useState<Schedule[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);

  // Determine which mode we're in:
  // 1. New split mode: tournamentConfig + clubSchedules (separate slices)
  // 2. Legacy combined mode: scheduleSelection (single slice with clubSchedules)
  // 3. Old legacy mode: clubSelection + schedule
  const isNewSplitMode = Boolean(
    tournamentConfig?.signupDeadlineDate &&
    clubSchedules?.clubSchedules?.length,
  );
  const isLegacyCombinedMode = Boolean(
    scheduleSelection?.clubSchedules?.length,
  );
  const isNewScheduleMode = isNewSplitMode || isLegacyCombinedMode;

  // Get club info based on mode
  const getClubsInfo = () => {
    if (isNewSplitMode && clubSchedules?.clubSchedules?.length) {
      return clubSchedules.clubSchedules.map((cs) => cs.club);
    }
    if (isLegacyCombinedMode && scheduleSelection?.clubSchedules?.length) {
      return scheduleSelection.clubSchedules.map((cs) => cs.club);
    }
    if (clubSelection?.selectedClub) {
      return [clubSelection.selectedClub];
    }
    return [];
  };

  const clubs = getClubsInfo();
  const totalClubs = clubs.length;
  const sportUid = sport?.sport?.uid;

  // Get all schedule IDs
  const getAllScheduleIds = () => {
    if (isNewSplitMode && clubSchedules?.clubSchedules?.length) {
      return clubSchedules.clubSchedules.flatMap((cs) => cs.scheduleIds);
    }
    if (isLegacyCombinedMode && scheduleSelection?.clubSchedules?.length) {
      return scheduleSelection.clubSchedules.flatMap((cs) => cs.scheduleIds);
    }
    return [];
  };

  const allScheduleIds = getAllScheduleIds();

  // Get signup deadline, match duration and min schedules required
  const signupDeadlineDate = isNewSplitMode
    ? tournamentConfig?.signupDeadlineDate
    : isLegacyCombinedMode
      ? scheduleSelection?.signupDeadlineDate
      : schedule?.signupDeadlineDate;

  const matchDuration = isNewSplitMode
    ? tournamentConfig?.matchDuration
    : isLegacyCombinedMode
      ? scheduleSelection?.matchDuration
      : schedule?.matchDuration;

  // minSchedulesRequired is only present in the new split mode (tournamentConfig slice)
  const minSchedulesRequired = isNewSplitMode
    ? (tournamentConfig?.minSchedulesRequired ?? 1)
    : null;

  // Load schedules when in new mode
  useEffect(() => {
    const loadSchedules = async () => {
      if (
        !isNewScheduleMode ||
        clubs.length === 0 ||
        allScheduleIds.length === 0
      ) {
        setLoadedSchedules([]);
        return;
      }

      setIsLoadingSchedules(true);
      try {
        const allLoadedSchedules: Schedule[] = [];
        for (const club of clubs) {
          const clubSchedulesList = await getSchedulesByClub(club.id);
          const selectedFromClub = clubSchedulesList.filter((s) =>
            allScheduleIds.includes(s.id),
          );
          allLoadedSchedules.push(...selectedFromClub);
        }
        setLoadedSchedules(allLoadedSchedules);
      } catch (error) {
        console.error('Error loading schedules for summary:', error);
        setLoadedSchedules([]);
      } finally {
        setIsLoadingSchedules(false);
      }
    };

    loadSchedules();
  }, [
    isNewScheduleMode,
    JSON.stringify(clubs.map((c) => c.id)),
    JSON.stringify(allScheduleIds),
  ]);

  // Check if all required data is present
  // LONG has no block ids: its schedule is the start day plus the weekly grid.
  const hasScheduleData = isLong
    ? Boolean(clubSchedules?.weekStartDate && clubSchedules?.weeklyGridId)
    : isNewScheduleMode
      ? allScheduleIds.length > 0
      : Boolean(schedule?.signupDeadlineDate);

  const hasClubData = isNewScheduleMode
    ? clubs.length > 0
    : Boolean(clubSelection?.selectedClub);

  const isComplete = Boolean(
    sport?.sport &&
    basicInfo?.name &&
    teamConfig?.gender &&
    pricing?.price &&
    hasClubData &&
    hasScheduleData,
  );

  const sanitizeHTML = (html: string) => ({
    __html: DOMPurify.sanitize(html),
  });

  // Calculate total time blocks from loaded schedules
  const totalTimeBlocks = loadedSchedules.reduce((acc, s) => {
    const blocks =
      s.schedule?.reduce(
        (sum, entry) => sum + (entry.timeRange?.length ?? 0),
        0,
      ) ?? 0;
    return acc + blocks;
  }, 0);

  // Compute tournament start date (earliest) for display in summary
  const tournamentStartDateText = useMemo(() => {
    if (isLong) {
      return clubSchedules?.weekStartDate
        ? formatScheduleDateForSummary(clubSchedules.weekStartDate)
        : null;
    }
    if (isNewScheduleMode && loadedSchedules.length > 0) {
      const allDates: string[] = [];
      loadedSchedules.forEach((s) => {
        s.schedule?.forEach((entry) => {
          if (entry.date) allDates.push(entry.date);
        });
      });
      if (allDates.length > 0) {
        const earliest = [...allDates].sort()[0];
        return formatScheduleDateForSummary(earliest);
      }
    }
    if (!isNewScheduleMode && schedule?.schedule?.length) {
      const allDates: string[] = [];
      schedule.schedule.forEach((fs) => {
        fs.schedule?.forEach((entry) => {
          if (entry.date) allDates.push(entry.date);
        });
      });
      if (allDates.length > 0) {
        const earliest = [...allDates].sort()[0];
        return formatScheduleDateForSummary(earliest);
      }
    }
    return null;
  }, [isLong, clubSchedules?.weekStartDate, isNewScheduleMode, loadedSchedules, schedule?.schedule]);

  // Compute tournament end date (latest) for display in summary
  const tournamentEndDateText = useMemo(() => {
    if (isNewScheduleMode && loadedSchedules.length > 0) {
      const allDates: string[] = [];
      loadedSchedules.forEach((s) => {
        s.schedule?.forEach((entry) => {
          if (entry.date) allDates.push(entry.date);
        });
      });
      if (allDates.length > 0) {
        const latest = [...allDates].sort().at(-1)!;
        return formatScheduleDateForSummary(latest);
      }
    }
    if (!isNewScheduleMode && schedule?.schedule?.length) {
      const allDates: string[] = [];
      schedule.schedule.forEach((fs) => {
        fs.schedule?.forEach((entry) => {
          if (entry.date) allDates.push(entry.date);
        });
      });
      if (allDates.length > 0) {
        const latest = [...allDates].sort().at(-1)!;
        return formatScheduleDateForSummary(latest);
      }
    }
    return null;
  }, [isNewScheduleMode, loadedSchedules, schedule?.schedule]);

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div
        data-guide-id="wizard.summary.review"
        className={`flex items-center gap-3 rounded-lg p-4 ${
          isComplete
            ? 'border border-green-200 bg-green-50'
            : 'border border-amber-200 bg-amber-50'
        }`}
      >
        {isComplete ? (
          <>
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">
                ¡Todo listo para crear el torneo!
              </p>
              <p className="text-sm text-green-700">
                Revisa los datos y haz clic en "Crear Torneo"
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                Faltan datos por completar
              </p>
              <p className="text-sm text-amber-700">
                Vuelve a los pasos anteriores para completar la información
              </p>
            </div>
          </>
        )}
      </div>

      {/* Sport & Basic Info */}
      <Card data-guide-id="wizard.summary.editable">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5" />
            Información del torneo
            <FieldHelp topicId="wizard.summary.editable" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Banner preview */}
          {config.showBanner && basicInfo?.bannerUrl && (
            <div className="overflow-hidden rounded-lg">
              <img
                src={basicInfo.bannerUrl}
                alt="Banner del torneo"
                className="h-32 w-full object-cover"
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryItem label="Deporte" value={sport?.sport?.name ?? '-'} />
            <SummaryItem label="Nombre" value={basicInfo?.name ?? '-'} />
          </div>

          {config.showPrizes && basicInfo?.prizes && (
            <div>
              <p className="text-sm font-medium text-gray-500">Premios</p>
              <div
                className="prose-sm mt-1 max-w-none text-gray-900"
                dangerouslySetInnerHTML={sanitizeHTML(basicInfo.prizes)}
              />
            </div>
          )}

          {config.showDescription && descriptionPreview && (
            <div>
              <p className="text-sm font-medium text-gray-500">Descripción</p>
              <div
                className="prose-sm mt-1 max-w-none text-gray-900"
                dangerouslySetInnerHTML={sanitizeHTML(descriptionPreview)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5" />
            Configuración de equipos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryItem
              label="Género"
              value={
                teamConfig?.gender
                  ? getGenderLabel(teamConfig.gender as Gender)
                  : '-'
              }
            />
            <SummaryItem
              label="Jugadores por equipo"
              value={teamConfig?.playersPerTeam?.toString() ?? '-'}
            />
            <div className="col-span-full sm:col-span-2 lg:col-span-4">
              <p className="text-sm font-medium text-gray-500">
                Restricción de edad
              </p>
              <div className="mt-1 space-y-0.5 text-sm text-gray-900">
                {(() => {
                  const ages = effectiveAgesFromTeamConfig(teamConfig ?? null);
                  return getTournamentAgeDisplayLines(
                    ages.minAge,
                    ages.maxAge,
                  ).map((line, i) => <p key={i}>{line}</p>);
                })()}
              </div>
            </div>
          </div>
          {config.showCategories &&
            teamConfig?.minCategory &&
            teamConfig?.maxCategory && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-500">
                  {categorySumLabel ? 'Categorías' : 'Rango de categorías'}
                </p>
                <p className="mt-1 text-gray-900">
                  {categorySumLabel ??
                    `${teamConfig.minCategory.name} - ${teamConfig.maxCategory.name}`}
                </p>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Fixture Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5" />
            Formato del torneo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryItem
              label="Estructura (grupos / eliminatorias)"
              value={
                fixtureConfig?.fixtureType
                  ? getFixtureTypeLabel(fixtureConfig.fixtureType)
                  : '-'
              }
            />
            <SummaryItem
              label="Tipo de torneo"
              value={
                fixtureConfig?.format
                  ? getTournamentFormatLabel(fixtureConfig.format)
                  : '-'
              }
            />
            {/* A knockout-only tournament has no groups to describe (the wizard only offers it with SHORT). */}
            {fixtureConfig?.fixtureType !== FixtureType.KNOCKOUTS && (
              <>
                <SummaryItem
                  label="Equipos por grupo"
                  value={
                    fixtureConfig?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS
                      ? (fixtureConfig?.teamsPerGroups?.toString() ?? '-')
                      : '—'
                  }
                />
                <SummaryItem
                  label="Clasificados por grupo"
                  value={
                    fixtureConfig?.fixtureType === FixtureType.GROUPS_AND_KNOCKOUTS
                      ? (fixtureConfig?.classifiedPerGroups?.toString() ?? '-')
                      : '—'
                  }
                />
              </>
            )}
            {fixtureConfig?.format === TournamentFormat.AMERICAN ? (
              <>
                <SummaryItem
                  label="Cuadro final"
                  value={getAmericanFinalBracketLabel(
                    fixtureConfig?.americanFinalBracketSize,
                  )}
                />
                {fixtureConfig?.fixtureType ===
                  FixtureType.GROUPS_AND_KNOCKOUTS && (
                  <SummaryItem
                    label="Formato de partidos en el grupo"
                    value={getAmericanGroupFormatLabel(fixtureConfig)}
                  />
                )}
              </>
            ) : (
              showOverflowGroupClassificationField(draft) && (
                <SummaryItem
                  label="Grupos ocasionales de 4 (Corto): clasificados"
                  value={
                    fixtureConfig?.overflowGroupClassification ===
                    OverflowGroupClassification.EXTRA_CLASSIFIER
                      ? '3 por grupo'
                      : '2 por grupo (defecto)'
                  }
                />
              )
            )}
            <SummaryItem
              label="Mín. equipos"
              value={fixtureConfig?.minTeamsAllowed?.toString() ?? '-'}
            />
            <SummaryItem
              label="Máx. equipos"
              value={fixtureConfig?.maxTeamsAllowed?.toString() ?? '-'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <DollarSign className="h-5 w-5" />
            Precio e inscripción
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryItem
              label="Precio por equipo"
              value={
                pricing?.price?.value !== undefined
                  ? `$${pricing.price.value.toLocaleString('es-AR')} ARS`
                  : '-'
              }
            />
            <SummaryItem
              label="Método de pago"
              value={
                pricing?.paymentType
                  ? getPaymentTypeLabel(pricing.paymentType as PaymentType)
                  : '-'
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Club & Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5" />
            Sede y horarios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tournament config info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryItem
              label="Cierre de inscripción"
              value={
                signupDeadlineDate
                  ? formatDateForUser(
                      typeof signupDeadlineDate === 'string'
                        ? signupDeadlineDate
                        : signupDeadlineDate.toISOString(),
                    )
                  : '-'
              }
              icon={<Calendar className="h-4 w-4" />}
            />
            <SummaryItem
              label="Duración de partidos"
              value={matchDuration ? `${matchDuration} minutos` : '-'}
              icon={<Clock className="h-4 w-4" />}
            />
            {!isAmerican && minSchedulesRequired !== null && (
              <SummaryItem
                label="Mín. horarios por jugador"
                value={`${minSchedulesRequired} ${minSchedulesRequired === 1 ? 'horario' : 'horarios'}`}
                icon={<Clock className="h-4 w-4" />}
              />
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SummaryItem
              label="Fecha de inicio"
              value={tournamentStartDateText ?? '-'}
              icon={<Calendar className="h-4 w-4" />}
            />
            <SummaryItem
              label="Fecha de finalización"
              value={tournamentEndDateText ?? '-'}
              icon={<Calendar className="h-4 w-4" />}
            />
          </div>

          {/* LONG: weekly pattern (weeklyScheduleTemplate) instead of ID-based blocks */}
          {isLong && (
            <div className="space-y-4">
              {clubs.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {clubs.length === 1 ? 'Club sede' : `Clubs (${clubs.length})`}
                  </p>
                  <div className="mt-2 space-y-2">
                    {clubs.map((club) => (
                      <div
                        key={club.id}
                        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3"
                      >
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-gray-900">
                          {club.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {clubSchedules?.weeklyGridName && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Grilla semanal</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {clubSchedules.weeklyGridName}
                    {clubSchedules.weeklyGridConfig && ' · compartida con otros torneos'}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Horarios de la semana
                </p>
                {weeklyTemplate.length === 0 ? (
                  <p className="mt-2 text-sm text-amber-600">
                    No hay horarios configurados
                  </p>
                ) : (
                  <div className="mt-2 space-y-1">
                    {LONG_WEEK_DAYS.map(({ value, label }) => {
                      const ranges = mergeSlotRanges(
                        weeklyTemplate.filter((s) => s.dayOfWeek === value),
                      );
                      if (ranges.length === 0) return null;
                      return (
                        <div key={value} className="text-sm text-gray-700">
                          <span className="font-medium">{label}:</span>{' '}
                          {ranges
                            .map((r) => `${r.start}–${r.end}`)
                            .join(', ')}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Old mode: club list + schedule info from draft.schedule */}
          {!isLong && !isNewScheduleMode && (
            <>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {totalClubs === 1 ? 'Club sede' : `Clubs (${totalClubs})`}
                </p>
                <div className="mt-2 space-y-2">
                  {clubs.map((club) => (
                    <div
                      key={club.id}
                      className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3"
                    >
                      <Building2 className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-gray-900">
                        {club.name}
                      </span>
                      {(
                        sportUid
                          ? getCourtsCountForSport(club, sportUid)
                          : club.courts?.length
                      ) ? (
                        <Badge variant="secondary">
                          {sportUid
                            ? getCourtsCountForSport(club, sportUid)
                            : club.courts!.length}{' '}
                          canchas
                        </Badge>
                      ) : null}
                    </div>
                  ))}
                  {clubs.length === 0 && (
                    <p className="text-sm text-gray-500">
                      No hay club seleccionado
                    </p>
                  )}
                </div>
              </div>
              {schedule?.schedule && schedule.schedule.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Horarios configurados
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {schedule.schedule.map((s, idx) => (
                      <Badge key={idx} variant="secondary">
                        {s.schedule?.length ?? 0} días
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* New mode: clubs with their schedule blocks grouped */}
          {!isLong && isNewScheduleMode && (
            <div>
              <p className="text-sm font-medium text-gray-500">
                Clubs y bloques de horario ({allScheduleIds.length} bloque
                {allScheduleIds.length !== 1 ? 's' : ''})
              </p>

              {isLoadingSchedules ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando bloques de horario...
                </div>
              ) : loadedSchedules.length > 0 ? (
                <ScheduleBlocksSummary
                  clubs={clubs}
                  schedules={loadedSchedules}
                  totalTimeBlocks={totalTimeBlocks}
                  sportUid={sportUid}
                />
              ) : (
                (() => {
                  const count =
                    scheduleSelection?.clubSchedules?.reduce(
                      (acc, cs) => acc + cs.scheduleIds.length,
                      0,
                    ) ?? 0;
                  return count > 0 ? (
                    <p className="mt-2 text-sm text-gray-500">
                      {count} bloque{count !== 1 ? 's' : ''} de horario
                      seleccionado{count !== 1 ? 's' : ''}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-amber-600">
                      No hay bloques de horario seleccionados
                    </p>
                  );
                })()
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SUMMARY ITEM
// ============================================================================

interface SummaryItemProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

function SummaryItem({ label, value, icon }: SummaryItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 flex items-center gap-2 text-gray-900">
        {icon}
        {value}
      </p>
    </div>
  );
}

// ============================================================================
// SCHEDULE BLOCKS SUMMARY (WITH EXPANDABLE DETAILS)
// ============================================================================

interface ScheduleBlocksSummaryProps {
  clubs: Club[];
  schedules: Schedule[];
  totalTimeBlocks: number;
  sportUid?: string | null;
}

function ScheduleBlocksSummary({
  clubs,
  schedules,
  totalTimeBlocks,
  sportUid,
}: ScheduleBlocksSummaryProps) {
  const [showDetails, setShowDetails] = useState(false);

  const formatDay = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  // Schedules grouped by club (preserve draft order of clubs)
  const schedulesByClub = clubs.map((club) => ({
    club,
    schedules: schedules.filter((s) => s.club?.id === club.id),
  }));

  return (
    <div className="mt-2 space-y-4">
      {schedulesByClub.map(({ club, schedules: clubSchedules }) => (
        <div key={club.id} className="space-y-2">
          {/* Club header */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-100/80 px-3 py-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-gray-900">{club.name}</span>
            {((sportUid
              ? getCourtsCountForSport(club, sportUid)
              : club.courts?.length) ?? 0) > 0 ? (
              <Badge variant="secondary">
                {sportUid
                  ? getCourtsCountForSport(club, sportUid)
                  : club.courts!.length}{' '}
                canchas
              </Badge>
            ) : null}
          </div>
          {/* Schedule blocks for this club */}
          <div className="ml-2 space-y-2 border-l-2 border-gray-200 pl-3">
            {clubSchedules.length === 0 ? (
              <p className="text-sm text-gray-500">
                Sin bloques de horario seleccionados
              </p>
            ) : (
              clubSchedules.map((s) => {
                const timeBlockCount =
                  s.schedule?.reduce(
                    (sum, entry) => sum + (entry.timeRange?.length ?? 0),
                    0,
                  ) ?? 0;
                const dayCount = s.schedule?.length ?? 0;

                return (
                  <div
                    key={s.id}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-gray-900">
                          {s.name || 'Sin nombre'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {dayCount} {dayCount === 1 ? 'día' : 'días'}
                        </Badge>
                        <Badge variant="outline">
                          {timeBlockCount}{' '}
                          {timeBlockCount === 1 ? 'horario' : 'horarios'}
                        </Badge>
                      </div>
                    </div>

                    {showDetails && s.schedule?.length ? (
                      <div className="mt-3 space-y-2">
                        {[...s.schedule]
                          .sort((a, b) => a.date.localeCompare(b.date))
                          .map((entry) => (
                            <div
                              key={`${s.id}-${entry.date}`}
                              className="space-y-1 rounded-md bg-white p-2 text-xs"
                            >
                              <p className="font-medium text-gray-700">
                                {formatDay(entry.date)}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {entry.timeRange.map((tr, idx) => (
                                  <Badge
                                    key={`${s.id}-${entry.date}-${idx}`}
                                    variant="outline"
                                    className="flex items-center gap-1"
                                  >
                                    <Clock className="h-3 w-3" />
                                    <span>
                                      {tr.startTime} - {tr.endTime}
                                    </span>
                                    <span className="text-[10px] text-gray-500">
                                      · {tr.courtsQuantity}{' '}
                                      {tr.courtsQuantity === 1
                                        ? 'cancha'
                                        : 'canchas'}
                                    </span>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ))}

      {/* Total summary + toggle */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Total: {schedules.length} bloque
          {schedules.length !== 1 ? 's' : ''} de horario con {totalTimeBlocks}{' '}
          horarios
        </span>
        <button
          type="button"
          className="text-xs font-medium text-blue-600 hover:underline"
          onClick={() => setShowDetails((prev) => !prev)}
        >
          {showDetails
            ? 'Ocultar detalle de horarios'
            : 'Ver detalle de todos los horarios'}
        </button>
      </div>
    </div>
  );
}

export default SummaryStep;
